import { differenceInDays, differenceInHours } from 'date-fns';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconAlert2 } from '#src/atoms/icons/system/iconAlert2.ts';
import { Actions as ActionsLayout } from '#src/atoms/layout/Actions/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, HeadlineS } from '#src/atoms/typography/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { useTickerContext } from '#src/contexts/ticker/index.tsx';
import { useLocalStorage } from '#src/hooks/useLocalStorage.ts';

import { JonumKycPromptDialog } from '../JonumVerificationDialog/JonumKycPromptDialog.tsx';
import { useJonumVerificationGuard } from '../JonumVerificationDialog/useJonumVerificationGuard.ts';

const WARNING_THRESHOLD_DAYS = 7;
const DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000;

const Content = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit);
`;

const AlertIcon = styled.span`
  color: var(--c-yellow-400);
  font-size: 32px;
`;

const Texts = styled(Vertical).attrs({ gap: 1 })`
  width: 100%;
`;

const Actions = styled(ActionsLayout).attrs({
  direction: 'vertical',
  gap: 1,
})`
  width: 100%;
`;

export const JonumGracePeriodWarningDialog = () => {
  const {
    kycReminderEligible,
    kycRequired,
    kycUnderReview,
    gracePeriodExpiresAt,
  } = useJonumVerificationGuard();
  const { now } = useTickerContext();
  const { formatMessage } = useIntl();
  const [dismissedAt, setDismissedAt] = useLocalStorage(
    'jonumGracePeriodWarningDismissedAt',
    0
  );
  const [dismissed, setDismissed] = useState(
    () => now.getTime() - (dismissedAt ?? 0) < DISMISS_COOLDOWN_MS
  );
  const [kycOpen, setKycOpen] = useState(false);

  const daysLeft = gracePeriodExpiresAt
    ? differenceInDays(gracePeriodExpiresAt, now)
    : null;

  const open =
    kycReminderEligible &&
    kycRequired &&
    !kycUnderReview &&
    !dismissed &&
    daysLeft !== null &&
    daysLeft >= 0 &&
    daysLeft < WARNING_THRESHOLD_DAYS;

  // Keep the dialog mounted once shown so dismissing it (or completing KYC)
  // animates the close; only unmount after the close transition.
  const [mounted, setMounted] = useState(open);
  if (open && !mounted) setMounted(true);

  if (!mounted) return null;

  const hoursLeft = gracePeriodExpiresAt
    ? Math.max(differenceInHours(gracePeriodExpiresAt, now), 1)
    : 1;
  const remaining =
    daysLeft !== null && daysLeft >= 1
      ? formatMessage(
          { defaultMessage: '{days, plural, one {# day} other {# days}}' },
          { days: daysLeft }
        )
      : formatMessage(
          { defaultMessage: '{hours, plural, one {# hour} other {# hours}}' },
          { hours: hoursLeft }
        );

  const handleClose = () => {
    setDismissedAt(Date.now());
    setDismissed(true);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        onClosed={() => setMounted(false)}
        hideHeader
        maxWidth="xs"
        fullWidth
        fullScreen={false}
      >
        <Content>
          <Horizontal center>
            <AlertIcon>
              <FontAwesomeIcon icon={iconAlert2} />
            </AlertIcon>
          </Horizontal>
          <Texts>
            <HeadlineS bold color="var(--c-white)">
              <FormattedMessage defaultMessage="Verify your account" />
            </HeadlineS>
            <BodyM color="var(--c-nd-600)">
              <FormattedMessage
                defaultMessage="You have <b>{remaining}</b> left to verify your identity. Keep playing, but some rewards won't be awarded. After this deadline, you'll no longer be able to take part in JONUM competitions."
                values={{
                  remaining,
                  b: chunks => <strong>{chunks}</strong>,
                }}
              />
            </BodyM>
          </Texts>
          <Actions>
            <Button
              size="medium"
              color="secondary"
              onClick={() => setKycOpen(true)}
            >
              <FormattedMessage defaultMessage="Verify my identity" />
            </Button>
            <Button size="medium" color="transparent" onClick={handleClose}>
              <FormattedMessage defaultMessage="Later" />
            </Button>
          </Actions>
        </Content>
      </Dialog>
      <JonumKycPromptDialog open={kycOpen} onDone={() => setKycOpen(false)} />
    </>
  );
};
